/* LOS Marine: a modern, digital proposal letter with a product inside. Big type, calm surfaces, very few elements. */
import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { KvitebergLogo } from "../KvitebergLogo";
import { fmtDate, monthLabel, useKeys, useMeta, useReveal } from "./proposalUtils";
import { downloadProposalPdf } from "../../pdf/download";
import { LosHomeStill } from "./los/LosHomeStill";
import { PhoneStill } from "./los/LosPhoneStill";
import { LiveSystem } from "./LiveSystem";
import { useCountUp } from "./motion";
import { nok } from "../../data/tilbod/priceModel";
import { QuoteShowcase } from "./QuoteShowcase";
import { SystemMockup } from "./los/LosSystemMockup";
import { WorkspaceMockup } from "./los/LosWorkspaceMockup";

/* >= 700 px: the real system is previewed inline under the letter. Below that, one button opens it full screen. */
const useWide = () => {
  const [wide, setWide] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(min-width: 700px)").matches : true));
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 700px)");
    const onChange = (e) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
};

const usePdf = (d) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadProposalPdf(d);
    } catch (e) {
      console.error("PDF", e);
      window.print();
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
};

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
};

/* top bar: the logo, the language, the PDF – nothing else */
const Top = ({ d }) => {
  const [busy, onPdf] = usePdf(d);
  const scrolled = useScrolled();
  const lt = d.langToggle;
  return (
    <div className={`kvl-top ${scrolled ? "is-scrolled" : ""}`} data-testid="tilbod-topbar">
      <div className="kvl-wide kvl-top-in">
        <a href="#cover" className="kvl-logo" aria-label="Kviteberg">
          <KvitebergLogo className="kvl-logo-svg" />
        </a>
        <div className="kvl-top-actions">
          {lt && (
            <div className="kvl-lang" role="group" aria-label="Language" data-testid="tilbod-lang-toggle">
              {lt.options.map(([code, label]) => (
                <button key={code} type="button" lang={code === "no" ? "nb" : code} className={code === lt.current ? "is-on" : ""} aria-pressed={code === lt.current} aria-label={label} onClick={() => lt.set(code)} data-testid={`tilbod-lang-${code}`}>
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="kvl-btn kvl-btn-sm" onClick={onPdf} disabled={busy} aria-busy={busy} data-testid="tilbod-print-btn">
            {busy ? d.ui.pdfBusy : d.ui.pdf}
          </button>
        </div>
      </div>
    </div>
  );
};

const Lines = ({ text, as: Tag = "h2", className, testid }) => (
  <Tag className={className} data-testid={testid}>
    {text.split("\n").map((l, i) => (
      <span key={l} className="kvl-line">
        {l}
        {i < text.split("\n").length - 1 && <br />}
      </span>
    ))}
  </Tag>
);

/* a section = one big heading + a reading column; optional wide figure below (or right after the heading). tone = paper | white | sand | dark */
const Sec = ({ id, head, eyebrow, title, children, figure, figureFirst, testid, tone = "paper", className = "" }) => {
  const ref = useReveal();
  return (
    <section id={id} className={`kvl-sec kvl-tone-${tone} ${className}`} data-head={head || (title ? title.split("\n")[0] : "")} data-tone={tone} data-testid={testid} ref={ref}>
      <div className="kvl-col kvl-reveal">
        {eyebrow && (
          <p className="kvl-sec-eyebrow" data-testid={`${testid}-eyebrow`}>
            {eyebrow}
          </p>
        )}
        {title && <Lines text={title} className="kvl-h2" testid={`${testid}-title`} />}
        {!figureFirst && children}
      </div>
      {figure && <div className="kvl-reveal kvl-reveal-2">{figure}</div>}
      {figureFirst && <div className="kvl-col kvl-reveal kvl-reveal-2 kvl-after">{children}</div>}
    </section>
  );
};

const Paras = ({ items, className = "kvl-p" }) =>
  (Array.isArray(items) ? items : [items]).filter(Boolean).map((x) => (
    <p key={x} className={className}>
      {x}
    </p>
  ));

/* a prototype moment: wide, minimal frame, one caption */
const Figure = ({ children, caption, testid, className = "", style }) => (
  <figure className={`kvl-figure ${className}`} style={style} data-testid={testid}>
    <div className="kvl-frame">{children}</div>
    <figcaption>{caption}</figcaption>
  </figure>
);

/* the hero: the claim on the left – one line of context, the headline, one sentence – and a still of the
   prototype on the right (one opportunity), bleeding off the edge of the page. No buttons, no numbers, no pills. Stacks below 1100 px. */
const Hero = ({ d, date, wide }) => {
  const month = monthLabel(d, date);
  const h = d.hero;
  /* written like the first line of a letter, not a badge: «Tilbud til Mediability AS, oktober 2026» */
  const kicker = `${d.ui.proposalTo} ${d.to.company}, ${d.lang === "en" ? month : month.toLowerCase()} ${date.getFullYear()}`;
  return (
    <header id="cover" className="kvl-hero kvl-hero-v3" data-head={d.ui.proposal} data-testid="tilbod-cover">
      <div className="kvl-hero-split">
        <div className="kvl-hero-copy">
          <p className="kvl-hero-kicker kvl-rise" data-testid="tilbod-eyebrow">
            {kicker}
          </p>
          <Lines text={d.title} as="h1" className="kvl-h1 kvl-rise" testid="tilbod-title" />
          <p className="kvl-hero-sub kvl-rise" style={{ "--d": "0.1s" }} data-testid="tilbod-subtitle">
            {h.sub}
          </p>
        </div>
        {wide ? (
          <div className="kvl-hero-shot kvl-rise" style={{ "--d": "0.22s" }} data-testid="tilbod-hero-shot">
            <div className="kvl-hero-shot-in">
              <div className="kvl-frame kvl-hero-frame">
                <LosHomeStill caption={d.ui.prototype} />
              </div>
            </div>
          </div>
        ) : (
          /* on a phone: the dashboard, as it looks on a phone, in a device-like card */
          <div className="kvl-hero-shot kvl-hero-shot-phone kvl-rise" style={{ "--d": "0.22s" }} data-testid="tilbod-hero-shot">
            <div className="kvl-hero-shot-in">
              <div className="kvl-phone-card">
                <PhoneStill />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

/* the letter: a greeting, the paragraphs, and the person who signs it */
const Letter = ({ d }) => {
  const ref = useReveal();
  return (
    <section id="letter" className="kvl-sec kvl-tone-white kvl-letter-sec" data-head={d.letter.greeting.replace(/,$/, "")} data-tone="white" data-testid="tilbod-letter" ref={ref}>
      <div className="kvl-col kvl-reveal">
        <p className="kvl-greeting" data-testid="tilbod-greeting">
          {d.letter.greeting}
        </p>
        <Paras items={d.letter.p} />
        <div className="kvl-sign" data-testid="tilbod-sign">
          <p className="kvl-p kvl-sign-close">{d.letter.close}</p>
          <div className="kvl-signer">
            <img src={d.from.photo} alt="" className="kvl-signer-img" width="72" height="72" loading="lazy" data-testid="tilbod-signer-photo" />
            <div>
              <span className="kvl-signer-name">{d.from.person}</span>
              <span className="kvl-signer-role">{d.from.company}</span>
              <a className="kvl-signer-mail" href={`mailto:${d.from.email}`} data-testid="tilbod-signer-mail">
                {d.from.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Flow = ({ steps }) => (
  <ol className="kvl-col kvl-steps" data-testid="tilbod-flow-steps">
    {steps.map(([k, v], i) => (
      <li key={k} data-testid={`tilbod-flow-${i + 1}`}>
        <span className="kvl-step-n">{String(i + 1).padStart(2, "0")}</span>
        <h3 className="kvl-h3">{k}</h3>
        <p className="kvl-p">{v}</p>
      </li>
    ))}
  </ol>
);

/* the pricing section, in reading order: what LOS Marine pays (the package, two big figures, then the first year as one
   proportional bar) → what is in each module → how the price adds up (a receipt). Every amount comes from price.model. */
const Tick = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" className="kvl-tick">
    <path d="M3 8.5 6.4 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* true once the element has been on screen (immediately when motion is reduced) */
const useSeen = () => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) {
        setSeen(true);
        io.disconnect();
      }
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
};

const Was = ({ was, save, testWas, testSave }) => (
  <span className="kvl-px-was">
    <s data-testid={testWas}>{was}</s>
    <span className="kvl-px-save" data-testid={testSave}>
      {save}
    </span>
  </span>
);

/* the package: establishment and licence side by side, then the first year */
const Package = ({ pr }) => {
  const P = pr.model;
  const k = pr.pkg;
  const y = pr.year;
  const [ref, seen] = useSeen();
  useCountUp(ref, seen, { delay: 120, duration: 1300, stagger: 140 });
  const parts = [
    ...P.payments.map((v, i) => ({ key: `p${i + 1}`, label: y.steps[i], meta: `${P.split[i]} %`, v })),
    { key: "lic", label: y.licence, meta: `${P.months} × ${nok(P.monthly)}`, v: P.licenceYear },
  ];
  return (
    <section ref={ref} className={`kvl-px-pkg ${seen ? "is-in" : ""}`} aria-label={k.tag} data-testid="tilbod-price-total">
      <header className="kvl-px-pkg-head">
        <span className="kvl-mono kvl-px-tag">{k.tag}</span>
        <span className="kvl-px-vat" data-testid="tilbod-price-vat">
          {pr.vat}
        </span>
      </header>

      <div className="kvl-px-figs">
        <div className="kvl-px-fig" data-testid="tilbod-price-total-oneoff">
          <span className="kvl-px-fig-label">{k.oneOff.label}</span>
          <span className="kvl-px-fig-value">
            <span data-num="" data-testid="tilbod-price-total-offer">
              {nok(P.offer)}
            </span>
          </span>
          <Was was={nok(P.list)} save={`−${nok(P.introDiscount)} ${k.introDiscount}`} testWas="tilbod-price-total-list" testSave="tilbod-price-total-discount" />
          <span className="kvl-px-fig-sub">{k.oneOff.sub}</span>
        </div>
        <div className="kvl-px-fig is-licence" data-testid="tilbod-price-total-monthly">
          <span className="kvl-px-fig-label">{k.licence.label}</span>
          <span className="kvl-px-fig-value">
            <span data-num="" data-testid="tilbod-price-total-monthly-value">
              {nok(P.monthly)}
            </span>
            <span className="kvl-px-per">{pr.perMonth}</span>
          </span>
          <Was was={`${nok(P.monthlySeparate)} ${k.separately}`} save={`−${nok(P.packageDiscount)} ${k.packageDiscount}`} testWas="tilbod-price-total-monthly-list" testSave="tilbod-price-total-monthly-discount" />
          <span className="kvl-px-fig-sub">{k.licence.sub}</span>
        </div>
      </div>

      <div className="kvl-px-year" data-testid="tilbod-price-total-year">
        <div className="kvl-px-year-head">
          <span className="kvl-mono kvl-px-tag">{y.title}</span>
          <span className="kvl-px-year-total">
            <span className="kvl-px-year-k">{y.totalLabel}</span>
            <b data-num="" data-testid="tilbod-price-year-value">
              {nok(P.firstYear)}
            </b>
          </span>
        </div>
        <div className="kvl-px-bar" role="img" aria-label={`${y.title}: ${parts.map((p) => `${p.label} ${nok(p.v)}`).join(", ")}`}>
          {parts.map((p, i) => (
            <span key={p.key} className="kvl-px-seg" data-part={p.key} style={{ flexGrow: p.v, "--i": i }} />
          ))}
        </div>
        <ol className="kvl-px-legend" data-testid="tilbod-price-year-parts">
          {parts.map((p) => (
            <li key={p.key} data-part={p.key} data-testid={`tilbod-price-year-${p.key}`}>
              <span className="kvl-px-legend-k">
                <i aria-hidden="true" />
                {p.label}
              </span>
              <span className="kvl-px-legend-v">{nok(p.v)}</span>
              <span className="kvl-px-legend-meta">{p.meta}</span>
            </li>
          ))}
        </ol>
        <p className="kvl-px-after">
          {y.after}{" "}
          <a href="#terms" className="kvl-px-link" data-testid="tilbod-price-total-pay">
            {pr.total.payLabel} og vilkår →
          </a>
        </p>
      </div>
    </section>
  );
};

/* a module: what is in it – its standalone price is a footnote, the package above is the answer */
const ModuleCard = ({ pr, m }) => (
  <article className="kvl-px-mod" data-mod={m.key} data-testid={`tilbod-price-card-${m.key}`}>
    <header className="kvl-px-mod-head">
      <span className="kvl-mono kvl-px-mod-tag">
        <b>{m.n}</b> {m.tag}
      </span>
      <h3 className="kvl-h3 kvl-px-mod-name">{m.name}</h3>
      <p className="kvl-px-mod-sub">{m.sub}</p>
    </header>
    <ul className="kvl-px-mod-features" aria-label={pr.includesLabel}>
      {m.features.map((f) => (
        <li key={f}>
          <Tick />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <footer className="kvl-px-mod-foot">
      <span className="kvl-mono kvl-px-mod-label">{pr.standalone}</span>
      <span className="kvl-px-mod-price">
        <span data-testid={`tilbod-price-${m.key}-offer`}>{m.offer}</span>
        <s className="kvl-px-mod-list" data-testid={`tilbod-price-${m.key}-list`} aria-label={`${pr.listLabel} ${m.list}`}>
          {m.list}
        </s>
        <span className="kvl-px-mod-plus" aria-hidden="true">
          +
        </span>
        <span data-testid={`tilbod-price-${m.key}-monthly`}>
          {m.monthly} {pr.perMonth}
        </span>
      </span>
    </footer>
  </article>
);

/* the receipt: the arithmetic in plain sight, so nobody has to add it up themselves */
const Receipt = ({ pr }) => {
  const P = pr.model;
  const rc = pr.receipt;
  const byKey = Object.fromEntries(pr.modules.map((m) => [m.key, m]));
  return (
    <section className="kvl-px-receipt" aria-labelledby="kvl-px-receipt-h" data-testid="tilbod-price-receipt">
      <div className="kvl-px-receipt-copy">
        <h3 id="kvl-px-receipt-h" className="kvl-h4">
          {rc.title}
        </h3>
        <p className="kvl-px-receipt-note">{rc.note}</p>
      </div>
      <table className="kvl-px-table">
        <thead>
          <tr>
            <th scope="col">{rc.cols[0]}</th>
            <th scope="col" className="is-num">
              {rc.cols[1]}
            </th>
            <th scope="col" className="is-num">
              {rc.cols[2]}
            </th>
          </tr>
        </thead>
        <tbody>
          {P.modules.map((m) => (
            <tr key={m.key} data-testid={`tilbod-receipt-${m.key}`}>
              <th scope="row">
                <span className="kvl-mono kvl-px-n">{byKey[m.key].n}</span>
                {byKey[m.key].name}
              </th>
              <td className="is-num">
                <s className="kvl-px-table-list">{nok(m.list)}</s>
                {nok(m.offer)}
              </td>
              <td className="is-num">{nok(m.monthly)}</td>
            </tr>
          ))}
          <tr className="is-sum" data-testid="tilbod-receipt-separate">
            <th scope="row">{rc.separate}</th>
            <td className="is-num">{nok(P.offer)}</td>
            <td className="is-num">{nok(P.monthlySeparate)}</td>
          </tr>
          <tr className="is-discount" data-testid="tilbod-receipt-discount">
            <th scope="row">{rc.packageDiscount}</th>
            <td className="is-num is-none">–</td>
            <td className="is-num">−{nok(P.packageDiscount)}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr data-testid="tilbod-receipt-pay">
            <th scope="row">{rc.pay}</th>
            <td className="is-num" data-testid="tilbod-receipt-pay-offer">
              {nok(P.offer)}
            </td>
            <td className="is-num" data-testid="tilbod-receipt-pay-monthly">
              {nok(P.monthly)}
              <span className="kvl-px-per">{pr.perMonth}</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
};

const Pricing = ({ d }) => {
  const pr = d.price;
  return (
    <div className="kvl-px" data-testid="tilbod-pricing">
      <Package pr={pr} />
      <div className="kvl-px-mods" data-testid="tilbod-price-cards">
        {pr.modules.map((m) => (
          <ModuleCard key={m.key} pr={pr} m={m} />
        ))}
      </div>
      <Receipt pr={pr} />
    </div>
  );
};

/* terms: the payment plan as one proportional bar (50 / 40 / 10) with three milestones, then the terms as a calm two-column list */
const Terms = ({ d, valid }) => {
  const tm = d.terms;
  const when = fmtDate(valid, d.lang);
  return (
    <div className="kvl-wide kvl-terms-wrap" data-testid="tilbod-terms-body">
      <div className="kvl-pay" data-testid="tilbod-payment">
        <h3 className="kvl-h4 kvl-pay-h">{tm.paymentTitle}</h3>
        <div className="kvl-pay-bar" aria-hidden="true">
          {tm.payment.map((p, i) => (
            <span key={p.k} style={{ flexGrow: p.pct }} data-i={i + 1} />
          ))}
        </div>
        <ol className="kvl-pay-steps">
          {tm.payment.map((p, i) => (
            <li key={p.k} data-i={i + 1} data-testid={`tilbod-payment-${i + 1}`}>
              <span className="kvl-pay-pct">{p.pct} %</span>
              <span className="kvl-pay-k">{p.k}</span>
              <span className="kvl-pay-v">{p.v}</span>
              <span className="kvl-mono kvl-pay-amount">{p.amount}</span>
            </li>
          ))}
        </ol>
        <p className="kvl-small kvl-pay-note">{tm.paymentNote}</p>
      </div>
      <dl className="kvl-terms-grid" data-testid="tilbod-terms">
        {tm.items.map(([k, v], i) => (
          <div key={k} data-testid={`tilbod-term-${i + 1}`}>
            <dt>{k}</dt>
            <dd>{v.replace("{validUntil}", when)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/* who we are: the claim and the text on the left, the four of us on the right */
const About = ({ d }) => {
  const ab = d.about;
  const ref = useReveal();
  return (
    <section id="about" className="kvl-sec kvl-tone-paper kvl-about-sec" data-head={ab.title.split(" ").slice(0, 2).join(" ")} data-tone="paper" data-testid="tilbod-about" ref={ref}>
      <div className="kvl-wide kvl-about-grid">
        <div className="kvl-about-text kvl-reveal">
          <Lines text={ab.title} className="kvl-h2" testid="tilbod-about-title" />
          <div className="kvl-about">
            <Paras items={ab.p} />
          </div>
          {ab.checks && (
            <ul className="kvl-checks" data-testid="tilbod-about-checks">
              {ab.checks.map((x) => (
                <li key={x}>
                  <Check aria-hidden="true" /> {x}
                </li>
              ))}
            </ul>
          )}
          <p className="kvl-p kvl-refs" data-testid="tilbod-refs">
            <span className="kvl-mono">{ab.refsLabel}</span>
            {ab.refItems.map((c, i) => (
              <a key={c.id} href={c.url} target="_blank" rel="noreferrer" data-testid={`tilbod-ref-link-${i + 1}`}>
                {c.k} <span aria-hidden="true">↗</span>
              </a>
            ))}
            {ab.site && (
              <a href={ab.site.url} target="_blank" rel="noreferrer" className="kvl-refs-site" data-testid="tilbod-about-site">
                {ab.site.label} <span aria-hidden="true">↗</span>
              </a>
            )}
          </p>
        </div>
        <ul className="kvl-team kvl-reveal kvl-reveal-2" data-testid="tilbod-team">
          {ab.team.map((m, i) => (
            <li key={m.name} data-testid={`tilbod-team-${i + 1}`}>
              <img src={m.img} alt="" width="600" height="660" loading="lazy" decoding="async" />
              <strong>{m.name}</strong>
              <span>{m.role}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* the way forward: short message, download pdf, who to contact (optional agenda renders when the data has one; cta button is optional) */
const Next = ({ d, valid }) => {
  const nx = d.next;
  const [busy, onPdf] = usePdf(d);
  const mail = nx.cta ? `mailto:${d.from.email}?subject=${encodeURIComponent(nx.subject)}${nx.body ? `&body=${encodeURIComponent(nx.body)}` : ""}` : null;
  return (
    <>
      <Paras items={nx.p} />
      {nx.agenda?.length ? (
        <div className="kvl-agenda-wrap" data-testid="tilbod-next-agenda">
          {nx.agendaTitle && <span className="kvl-mono kvl-agenda-h">{nx.agendaTitle}</span>}
          <ol className="kvl-agenda">
            {nx.agenda.map(([tm, what, sub], i) => (
              <li key={what} data-testid={`tilbod-next-agenda-${i + 1}`}>
                <span className="kvl-mono kvl-agenda-t">{tm}</span>
                <span className="kvl-agenda-what">{what}</span>
                {sub && <span className="kvl-agenda-sub">{sub}</span>}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
      {nx.after && <p className="kvl-p kvl-agenda-after">{nx.after}</p>}
      <div className="kvl-invite" data-testid="tilbod-next-invite">
        <div className="kvl-invite-actions">
          {nx.cta && mail && (
            <a href={mail} className="kvl-btn kvl-btn-primary" data-testid="tilbod-next-book-btn">
              {nx.cta}
            </a>
          )}
          <button type="button" className={`kvl-btn${nx.cta ? "" : " kvl-btn-primary"}`} onClick={onPdf} disabled={busy} aria-busy={busy} data-testid="tilbod-next-pdf-btn">
            {busy ? d.ui.pdfBusy : nx.pdf}
          </button>
        </div>
        {nx.ctaSub && <p className="kvl-small kvl-invite-sub">{nx.ctaSub}</p>}
      </div>
      <div className="kvl-closing" data-testid="tilbod-next-mail">
        {d.from.photo && <img src={d.from.photo} alt="" className="kvl-closing-photo" loading="lazy" />}
        <div className="kvl-closing-who">
          <span className="kvl-closing-name">{d.from.person}</span>
          <span className="kvl-closing-role">
            {d.from.role} · {d.from.company}
          </span>
          <a href={`mailto:${d.from.email}`}>{d.from.email}</a>
        </div>
        <span className="kvl-mono kvl-meta" data-testid="tilbod-meta">
          {d.ref} · {d.ui.validUntil} {fmtDate(valid, d.lang)}
        </span>
      </div>
    </>
  );
};

/* the roadmap: six weeks as one strip (four phases + the follow-up), four columns with what we do, what LOS brings and the result; then what is in the first delivery */
const Roadmap = ({ dl }) => {
  const weeks = Array.from({ length: dl.weeks }, (_, i) => i + 1);
  return (
    <div className="kvl-wide kvl-roadmap" data-testid="tilbod-plan">
      <div className="kvl-tl" style={{ "--weeks": dl.weeks }} data-testid="tilbod-plan-strip" aria-hidden="true">
        <div className="kvl-tl-weeks">
          {weeks.map((w) => (
            <span key={w} className="kvl-mono kvl-tl-w">
              <em>{dl.weekWord} </em>
              {w}
            </span>
          ))}
          <span className="kvl-mono kvl-tl-w kvl-tl-w-tail">{dl.tail.k}</span>
        </div>
        <div className="kvl-tl-bars">
          {dl.phases.map((ph, i) => (
            <span key={ph.name} className="kvl-tl-bar" data-phase={i + 1} style={{ gridColumn: `${ph.from} / ${ph.to + 1}`, "--i": i }}>
              <b>{String(i + 1).padStart(2, "0")}</b>
              <span>{ph.name}</span>
            </span>
          ))}
          <span className="kvl-tl-bar kvl-tl-tail" data-short={dl.tail.short} style={{ gridColumn: `${dl.weeks + 1} / ${dl.weeks + 2}`, "--i": dl.phases.length }}>
            <span>{dl.tail.name}</span>
          </span>
        </div>
      </div>
      <ol className="kvl-steps4">
        {dl.phases.map((ph, i) => (
          <li key={ph.name} data-testid={`tilbod-plan-${i + 1}`} data-phase={i + 1}>
            <span className="kvl-step4-n">{String(i + 1).padStart(2, "0")}</span>
            <span className="kvl-mono kvl-step4-week">{ph.week}</span>
            <h3 className="kvl-h4 kvl-step4-name">{ph.name}</h3>
            <p>{ph.p}</p>
            <dl className="kvl-step4-meta">
              <div>
                <dt>{dl.youLabel}</dt>
                <dd>{ph.you}</dd>
              </div>
              <div>
                <dt>{dl.resultLabel}</dt>
                <dd>{ph.result}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ol>
      {dl.after && (
        <p className="kvl-plan-after" data-testid="tilbod-plan-after">
          {dl.after}
        </p>
      )}
      {dl.note && (
        <p className="kvl-small kvl-plan-note" data-testid="tilbod-plan-note">
          {dl.note}
        </p>
      )}
      {dl.includedTitle && <h3 className="kvl-h3 kvl-included-h">{dl.includedTitle}</h3>}
      <dl className="kvl-included-grid" data-testid="tilbod-included">
        {dl.includedItems.map(([k, v]) => (
          <div key={k}>
            <dt>
              <Tick />
              {k}
            </dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="kvl-small kvl-outside">{dl.outside}</p>
    </div>
  );
};

export const ProductProposal = ({ d }) => {
  useKeys(d.pages);
  useMeta(`${d.ui.proposal} · ${d.to.company} – ${d.from.company}`);
  const wide = useWide();
  const [liveOpen, setLiveOpen] = useState(false);
  const date = d.issued ? new Date(`${d.issued}T12:00:00`) : new Date();
  const valid = new Date(date.getTime() + d.validDays * 86400000);
  const { idea, flow, ai, visma, delivery: dl, price: pr } = d;

  return (
    <div className="kvp kvl" lang={d.lang} data-testid="tilbod-page">
      <a className="kvp-skip" href="#letter">
        {d.ui.skip}
      </a>
      <Top d={d} />
      <main>
        <Hero d={d} date={date} wide={wide} />
        <Letter d={d} />
        <QuoteShowcase d={d} />
        <LiveSystem d={d} open={liveOpen} onOpenChange={setLiveOpen} compact={!wide} />

        <Sec id="idea" eyebrow={idea.eyebrow} title={idea.title} testid="tilbod-idea" className="kvl-sec-center" figure={<SystemMockup d={d} />}>
          <Paras items={idea.p} />
        </Sec>

        <Sec id="flow" title={flow.title} head={flow.head} testid="tilbod-flow" tone="white" figure={<Flow steps={flow.steps} />} className={flow.title ? "" : "kvl-sec-notitle"} />

        <Sec
          id="ai"
          title={ai.title}
          testid="tilbod-ai"
          tone="sand"
          figure={
            <>
              <p className="kvl-col kvl-example" data-testid="tilbod-ai-example">
                <span>{ai.example.from}</span>
                <span className="kvl-arrow" aria-hidden="true">
                  →
                </span>
                <span>{ai.example.to}</span>
              </p>
              <Figure caption={ai.example.caption} testid="tilbod-ai-figure" className="kvl-figure-mock">
                <WorkspaceMockup d={d} />
              </Figure>
            </>
          }
        >
          <Paras items={ai.p} />
          {ai.list && (
            <ul className="kvl-list" data-testid="tilbod-ai-list">
              {ai.list.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {ai.after && <p className="kvl-p kvl-emph">{ai.after}</p>}
        </Sec>

        <Sec id="visma" title={visma.title} testid="tilbod-visma" tone="white">
          <Paras items={visma.p} />
          <div className="kvl-stack" data-testid="tilbod-visma-chain">
            {visma.line.map((x, i) => (
              <div key={x} className="kvl-stack-node" data-testid={`tilbod-visma-node-${i + 1}`}>
                {i > 0 && (
                  <span className="kvl-stack-arrow" aria-hidden="true">
                    ↓
                  </span>
                )}
                <span className="kvl-stack-title">{x}</span>
                <span className="kvl-stack-sub">{visma.under[i]}</span>
              </div>
            ))}
          </div>
          {visma.after && <p className="kvl-p">{visma.after}</p>}
          <p className="kvl-small">{visma.note}</p>
        </Sec>

        <Sec id="delivery" title={dl.title} testid="tilbod-delivery" figure={<Roadmap dl={dl} />}>
          <p className="kvl-p">{dl.p}</p>
        </Sec>

        <Sec id="price" title={pr.title} testid="tilbod-price" tone="sand" figure={<Pricing d={d} />}>
          <Paras items={pr.intro || pr.p} />
        </Sec>

        {d.terms && (
          <Sec id="terms" title={d.terms.title} testid="tilbod-terms-sec" tone="white" figure={<Terms d={d} valid={valid} />}>
            <p className="kvl-p">{d.terms.p}</p>
          </Sec>
        )}

        {d.about && <About d={d} />}

        <Sec id="next" title={d.next.title} testid="tilbod-next" tone="white" className="kvl-sec-tight">
          <Next d={d} valid={valid} />
        </Sec>

        <footer className="kvl-foot kvl-tone-white" data-testid="tilbod-footer">
          <div className="kvl-wide kvl-foot-in">
            <div className="kvl-foot-brand">
              <a href="https://kviteberg.no" target="_blank" rel="noreferrer" className="kvl-foot-logo" aria-label="Kviteberg" data-testid="tilbod-footer-logo">
                <KvitebergLogo className="kvl-logo-svg" />
              </a>
              <span>
                {d.from.company} · {d.from.address}
              </span>
            </div>
            <span>
              {d.ref} · {d.ui.confidential}
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
};
